import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { NativeWriteDialog, type NativeWriteReview } from './native-write-dialog';
import type { RuntimeSelection } from './operation-client';

type Request = {
  operation: 'files.write' | 'collections.undo';
  input: unknown;
  documentNonce: string;
};
type Pending = { request: Request; review: NativeWriteReview; resolve(result: boolean): void };

export function useNativeWriteApproval({
  session,
  apiUrl,
  selection,
  nonce,
  title,
}: {
  session: typeof fetch;
  apiUrl(path: string): string;
  selection: RuntimeSelection;
  nonce: string;
  title: string;
}) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const active = useRef<Pending | null>(null);
  const loading = useRef(false);
  const confirming = useRef(false);
  const generation = useRef(0);
  const identity = JSON.stringify([selection, nonce]);
  useLayoutEffect(() => {
    generation.current++;
    setPending(null);
    setBusy(false);
    setFailed(false);
    confirming.current = false;
    return () => {
      generation.current++;
      active.current?.resolve(false);
      active.current = null;
    };
  }, [identity, session]);
  const send = useCallback(
    async (phase: 'review' | 'confirm', request: Request, reviewDigest?: string) => {
      const { artifactId, ...selected } = selection;
      const response = await session(
        apiUrl(`/api/artifacts/${encodeURIComponent(artifactId)}/runtime/write/${phase}`),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            ...selected,
            documentNonce: request.documentNonce,
            operation: request.operation,
            input: request.input,
            ...(reviewDigest ? { reviewDigest } : {}),
          }),
        },
      );
      if (!response.ok) throw new Error('app_write_review_refused');
      const body = await response.json();
      if (body?.ok !== true) throw new Error('app_write_review_refused');
      return body.result;
    },
    [session, apiUrl, identity],
  );
  const approve = useCallback(
    async (request: Request) => {
      if (!nonce || request.documentNonce !== nonce || active.current || loading.current)
        return false;
      const token = generation.current;
      loading.current = true;
      try {
        const review = (await send('review', request)) as NativeWriteReview;
        if (
          token !== generation.current ||
          review.operation !== request.operation ||
          !/^[a-f0-9]{64}$/.test(review.reviewDigest)
        )
          return false;
        setFailed(false);
        return await new Promise<boolean>((resolve) => {
          const next = { request, review, resolve };
          active.current = next;
          setPending(next);
        });
      } catch {
        return false;
      } finally {
        loading.current = false;
      }
    },
    [nonce, send],
  );
  const decide = async (approved: boolean) => {
    const now = active.current;
    if (!now || confirming.current) return;
    if (!approved) {
      active.current = null;
      setPending(null);
      now.resolve(false);
      return;
    }
    const token = generation.current;
    confirming.current = true;
    setBusy(true);
    try {
      const result = await send('confirm', now.request, now.review.reviewDigest);
      if (
        token !== generation.current ||
        active.current !== now ||
        typeof result?.receiptId !== 'string'
      )
        return;
      active.current = null;
      setPending(null);
      now.resolve(true);
    } catch {
      if (token === generation.current && active.current === now) {
        setFailed(true);
        now.resolve(false);
      }
    } finally {
      if (token === generation.current) {
        setBusy(false);
        confirming.current = false;
      }
    }
  };
  return {
    approve,
    dialog: pending ? (
      <NativeWriteDialog
        title={title}
        review={pending.review}
        busy={busy}
        failed={failed}
        decide={decide}
      />
    ) : null,
  };
}
